import { useEffect, useRef, useState, type ReactNode } from 'react';
import './brand-splash.css';
import { SPLASH_PATH, SPLASH_PATH_LENGTH } from './splash-path';

/**
 * Brand splash (owner 23.10.2026, design board 16f1): the red face line is drawn like a pencil
 * stroke (0.1–1.6 s), then FACE and 360° fade in over it (1.5–2.7 s, 2.2–3.5 s) while the bar under
 * «All You Need Is…» fills red. Plays once; `children` (e.g. language buttons or «Tap to start»)
 * appear when the mark is complete. A tap anywhere before that skips to the end.
 */
export const SPLASH_DONE_MS = 3_500;

export function BrandSplash({
  children,
  onDone,
  className,
}: {
  children?: ReactNode;
  onDone?: () => void;
  className?: string;
}) {
  const [done, setDone] = useState(false);
  const fired = useRef(false);
  useEffect(() => {
    if (!done) return;
    if (fired.current) return;
    fired.current = true;
    onDone?.();
  }, [done, onDone]);
  useEffect(() => {
    const t = window.setTimeout(() => setDone(true), SPLASH_DONE_MS);
    return () => window.clearTimeout(t);
  }, []);
  return (
    <div
      className={['brand-splash', done ? 'is-done' : '', className ?? ''].join(' ').trim()}
      onClick={() => setDone(true)}
      data-testid="brand-splash"
    >
      <div className="bs-logo" aria-label="360°FACE" role="img">
        <svg viewBox="0 0 481 1000" aria-hidden="true">
          <defs>
            <filter id="bs-pen3d" x="-10%" y="-5%" width="120%" height="110%">
              <feDropShadow dx="1.5" dy="1.5" stdDeviation="0" floodColor="#a3151b" />
              <feDropShadow dx="0" dy="4" stdDeviation="3" floodColor="#1d2329" floodOpacity=".22" />
            </filter>
          </defs>
          <path
            className="bs-pen"
            filter="url(#bs-pen3d)"
            d={SPLASH_PATH}
            style={{ strokeDasharray: SPLASH_PATH_LENGTH, strokeDashoffset: SPLASH_PATH_LENGTH }}
          />
        </svg>
        <img className="bs-face" src="/brand/splash-face.png" alt="" />
        <img className="bs-top" src="/brand/splash-top.png" alt="" />
      </div>
      <div className="bs-foot">
        <span className="bs-line">All You Need Is…</span>
        <span className="bs-bar" aria-hidden="true">
          <i />
        </span>
        <div className="bs-after">{children}</div>
      </div>
    </div>
  );
}
